/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { GlobalConstants } from '../../constants/global-constants';
import { Observable, Subject, catchError, first, of, switchMap, take, takeUntil } from 'rxjs';
import { SysquePopupConfig } from '../../models/sysque-popup-config';
import { createDialog } from '../../popup-dialog-factory/popup-dialog.facade';
import { TabDescription } from 'libs/sysque/toolbelt/data-access/src/lib/domain/tab-description';
import { Item } from 'libs/sysque/toolbelt/data-access/src/lib/domain/item';
import { TooltipItem } from '../../../../../data-access/src/lib/domain/tooltip-item';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import { SystemInfo } from '../../../../../data-access/src/lib/domain/system-info';
import {
  IManageModePanelFrameFacade,
  MANAGE_MODE_PANEL_FRAME_FACADE_TOKEN,
} from '../../../../../data-access/src/lib/domain/facades-models/manage-mode-panel-frame-facade-interface';
import { DxContextMenuComponent } from 'devextreme-angular/ui/context-menu';
import { SystemCategories } from '../../models/system-categories';
import { SysQueContextMenuComponent } from 'libs/sysque/shared/ui/src/lib/components/sysque-context-menu/sysque-context-menu.component';
import { ElectricalCategoryNames } from '../../../../../../shared/ui/src/lib/enums/electrical-category-names';
import { SysQueStateService } from '../../../../../utils/src/lib/sysque-electrical-state/sysque-elecrical-state.service';
import { SysQueCopyPasteService } from '../../../../../utils/src/lib/sysque-copy-paste/sysque-copy-paste.service';
import { LocalStorageService } from '../../../../../utils/src/lib/local-storage/local-storage.service';

@Component({
  selector: 'sysque-manage-src-mode-panel-frame',
  templateUrl: './manage-mode-src-panel-frame.component.html',
  styleUrls: ['./manage-mode-src-panel-frame.component.scss'],
  standalone: false
})
export class ManageModeSrcPanelFrameComponent implements OnInit, OnDestroy {
  @Input() showAddFittingBtn: boolean;
  @Input() isSystemSource: boolean;
  @Input() key: string;
  @Input() showRootPath: boolean;
  @Output() onCopyCategoryToSystem: EventEmitter<string> = new EventEmitter<string>();
  public categoryList$: Observable<TabDescription[]>;
  public fittingList$: Observable<Item[]>;
  public systemInfo$: Observable<SystemInfo[]>;
  public state: SysqueElectricalState = new SysqueElectricalState();
  public system: string = '';
  public defaultCategory: string;
  public menuItems: any[];
  public categoryMenuItems: any[];
  public destinationMenuItems: any[] = [{ text: GlobalConstants.Paste }];
  public isContextMenuVisible: boolean = this.system != '';
  public fittingList: Item[];
  public tooltipItems: TooltipItem[] = [];
  public stateSystemInfo$: Observable<SystemInfo[]>;
  public stateCategoryList$: Observable<TabDescription[]>;
  public stateFittingsList$: Observable<Item[]>;
  public selectedItems: Item[] = [];
  public categories = SystemCategories.getSystemCategories().slice(2, 7);
  public electricalCategoryNames = Object.values(ElectricalCategoryNames).map((value) => value.toLocaleLowerCase());
  private readonly closed$ = new Subject<void>();
  public selectedSystemIndex: number;
  public selectedCategoryIndex: number;

  constructor(
    @Inject(MANAGE_MODE_PANEL_FRAME_FACADE_TOKEN) public facade: IManageModePanelFrameFacade,
    private cd: ChangeDetectorRef,
    private sysqueStateService: SysQueStateService,
    public copyPasteService: SysQueCopyPasteService,
    private localStorageService: LocalStorageService
  ) {}

  public ngOnDestroy(): void {
    localStorage.removeItem(GlobalConstants.MANAGE_MODE_WINDOW);
    window.removeEventListener(GlobalConstants.STORAGE, this.storageEventListener.bind(this));
    this.closed$.next();
    this.closed$.complete();
  }

  public ngOnInit(): void {
    localStorage.setItem(GlobalConstants.MANAGE_MODE_WINDOW, 'true');
    window.addEventListener(GlobalConstants.STORAGE, this.storageEventListener.bind(this));
    this.sysqueStateService.setManageSrcState(this.sysqueStateService.getElectricalState());
    this.sysqueStateService.manageSrc$.pipe(takeUntil(this.closed$)).subscribe((state) => {
      if (localStorage.getItem(GlobalConstants.MANAGE_MODE_WINDOW)) {
        this.state = state;
        this.updateData();
      }
    });

    this.clearFittingsAndCategories();
    this.copyPasteService.setCopiedItemsList([]);
    this.copyPasteService.setUniqueCopiedItemsList([]);
    // Call initial updateData
    this.updateData();
  }

  private updateData(): void {
    this.system = this.state.selectedSystem;
    this.systemInfo$ = of(this.state.systemList);
    this.categoryList$ = of(this.state.categoryList);
    this.defaultCategory = this.state.selectedCategory;
    const copiedItemList = this.copyPasteService.getCopiedItemsList();

    if (Array.isArray(this.state.fittingList)) {
      this.state.fittingList.forEach((fitting) => {
        if (copiedItemList.includes(fitting)) {
          fitting.isCopyState = false;
        }
      });
    }

    this.fittingList = this.state.fittingList;
    this.fittingList$ = of(this.fittingList);
    this.menuItems = [
      { label: GlobalConstants.CreateSystem, disabled: false },
      { label: GlobalConstants.RenameSystem, disabled: this.state.selectedSystem === '' },
      { label: GlobalConstants.DeleteSystem, disabled: this.state.selectedSystem === '' },
    ];
    this.categoryMenuItems = [
      { label: GlobalConstants.CreateCategory, disabled: false },
      {
        label: GlobalConstants.RenameCategory,
        disabled: this.electricalCategoryNames.includes(this.state.selectedCategory.toLocaleLowerCase()),
      },
      {
        label: GlobalConstants.DeleteCategory,
        disabled: this.electricalCategoryNames.includes(this.state.selectedCategory.toLocaleLowerCase()),
      },
      { divider: true, disabled: true },
      { label: GlobalConstants.CopyCategoryToSystem, disabled: this.defaultCategory === '' },
    ];
    this.cd.detectChanges();
  }

  @ViewChildren(DxContextMenuComponent) contextMenus: QueryList<DxContextMenuComponent>;
  public onContextMenuShowing(event): void {
    this.contextMenus.forEach((contextMenu) => {
      if (contextMenu !== event.component) {
        contextMenu.visible = false;
      }
    });
  }

  @ViewChild('systemContextMenu') systemContextMenu: SysQueContextMenuComponent;
  @ViewChild('categoryContextMenu') categoryContextMenu: SysQueContextMenuComponent;
  public closeOtherContextMenu(currentContextMenu: SysQueContextMenuComponent): void {
    // Close other context menu
    [this.systemContextMenu, this.categoryContextMenu].forEach((contextMenu) => {
      if (contextMenu !== currentContextMenu) {
        contextMenu.hideContextMenu();
      }
    });
  }

  public updateCopiedItemList(e: Item): void {
    this.selectedItems = this.copyPasteService.getCopiedItemsList();
    if (!this.selectedItems.includes(e)) {
      this.selectedItems.push(e);
      this.copyPasteService.setCopiedItemsList(this.selectedItems);
    }
  }

  public onRightClick(e: MouseEvent): void {
    e.preventDefault();
  }

  public onValueChanged(e: any): void {
    const { value } = e;
    if (value === undefined || value === '') return;

    this.clearFittingsAndCategories();
    this.sysqueStateService.updateSrcProperty('selectedSystem', value);

    [1, 2].forEach((index) => (this.menuItems[index].disabled = this.state.selectedSystem === ''));

    this.retrieveCategories(value as string);
    this.setDefaultCategoryAndFittingList();
  }

  public clearFittingsAndCategories(): void {
    this.categoryList$ = of([]);
    this.fittingList$ = of([]);
    this.fittingList = [];
    this.copyPasteService.setCopiedItemsName([]);
    this.copyPasteService.setCopiedItemsList([]);
    this.copyPasteService.setDuplicateItemsList([]);
    this.copyPasteService.setUniqueCopiedItemsList([]);
  }

  private retrieveCategories(selectedSystem: string): void {
    this.categoryList$ = this.facade.notifyGetCategoryListAction(selectedSystem);
    this.categoryList$.pipe(take(1)).subscribe((categories) => {
      this.sysqueStateService.updateSrcProperty('categoryList', categories);
      this.state.categoryList = categories;
    });
  }

  public setDefaultCategoryAndFittingList(): void {
    this.categoryList$
      .pipe(
        first(),
        switchMap((categories) => {
          if (categories && categories.length > 0) {
            this.defaultCategory = categories[0].tabName;
            this.sysqueStateService.updateSrcProperty('selectedCategory', this.defaultCategory);

            const isElectricalCategory = this.electricalCategoryNames.includes(this.defaultCategory.toLocaleLowerCase());
        
            [1, 2].forEach((index) => (this.categoryMenuItems[index].disabled = isElectricalCategory));
          }
          return this.facade.getFittings(this.state.selectedSystem, this.defaultCategory);
        }),
        catchError((error) => {
          console.error('Error fetching fittings:', error);
          return [];
        }),
        take(1)
      )
      .subscribe((data) => {
        if (!data) {
          this.sysqueStateService.updateSrcProperty('fittingList', []);
          this.fittingList = [];
          this.updateState();
        } else {
          const fittingList = data.map((item) => {
            return {
              ...item,
              tooltipItems: this.facade.getTooltipItemInfo(item, this.defaultCategory),
            };
          });
          this.sysqueStateService.updateSrcProperty('fittingList', fittingList);
          this.fittingList = fittingList;
          this.updateState();
        }
      });
  }

  public onCategoryChanged(e: any): void {
    if (e.value === undefined || e.event === undefined) {
      return
    };
    setTimeout(() => {
      this.selectedCategoryIndex = this.state.categoryList.findIndex((x) => x.tabName === e.value);
    }, 0);
    this.clearFittingsAndCategories();
    this.fittingList$ = this.facade.notifyGetFittingsListAction(this.state.selectedSystem, e.value);
    this.fittingList$.pipe(takeUntil(this.closed$)).subscribe((response) => {
      this.sysqueStateService.updateSrcProperty('fittingList', response);
      this.fittingList = response;
      this.sysqueStateService.updateSrcProperty('selectedCategory', e.value);
      this.defaultCategory = e.value;
      this.categoryMenuItems[1].disabled = this.electricalCategoryNames.includes(
        this.state.selectedCategory.toLocaleLowerCase()
      );
      this.categoryMenuItems[1].disabled = this.electricalCategoryNames.includes(
        this.state.selectedCategory.toLocaleLowerCase()
      );
      this.updateState();
    });
  }

  public handleContextMenuItem(item: string): void {
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.state);
    this.selectedSystemIndex = this.state.systemList.findIndex((x) => x.name === this.state.selectedSystem);
    let sysquePopupConfig = new SysquePopupConfig();
    switch (item) {
      case GlobalConstants.CreateSystem:
        createDialog(sysquePopupConfig, GlobalConstants.CREATE_SYSTEM_NAME_DIALOG);
        this.facade.sendPopupRoutingAction(
          GlobalConstants.CREATE_SYSTEM_NAME_DIALOG,
          sysquePopupConfig.width,
          sysquePopupConfig.height
        );
        break;

      case GlobalConstants.RenameSystem:
        createDialog(sysquePopupConfig, GlobalConstants.RENAME_SYSTEM_NAME_DIALOG);
        this.facade.sendPopupRoutingAction(
          GlobalConstants.RENAME_SYSTEM_NAME_DIALOG,
          sysquePopupConfig.width,
          sysquePopupConfig.height
        );
        break;

      case GlobalConstants.DeleteSystem:
        createDialog(sysquePopupConfig, GlobalConstants.DELETE_SYSTEM_NAME_DIALOG);
        this.facade.sendPopupRoutingAction(
          GlobalConstants.DELETE_SYSTEM_NAME_DIALOG,
          sysquePopupConfig.width,
          sysquePopupConfig.height
        );
        break;

      default:
        break;
    }
  }

  public handleCategoryContextMenuItem(item: string): void {
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.state);
    let sysquePopupConfig = new SysquePopupConfig();
    switch (item) {
      case GlobalConstants.CreateCategory:
        createDialog(sysquePopupConfig, GlobalConstants.CREATE_CATEGORY_NAME_DIALOG);
        this.facade.sendPopupRoutingAction(
          GlobalConstants.CREATE_CATEGORY_NAME_DIALOG,
          sysquePopupConfig.width,
          sysquePopupConfig.height
        );
        break;
      case GlobalConstants.DeleteCategory:
        createDialog(sysquePopupConfig, GlobalConstants.DELETE_CATEGORY_NAME_DIALOG);
        this.facade.sendPopupRoutingAction(
          GlobalConstants.DELETE_CATEGORY_NAME_DIALOG,
          sysquePopupConfig.width,
          sysquePopupConfig.height
        );
        break;
      case GlobalConstants.RenameCategory:
        createDialog(sysquePopupConfig, GlobalConstants.RENAME_CATEGORY_NAME_DIALOG);
        this.facade.sendPopupRoutingAction(
          GlobalConstants.RENAME_CATEGORY_NAME_DIALOG,
          sysquePopupConfig.width,
          sysquePopupConfig.height
        );
        break;
      case GlobalConstants.CopyCategoryToSystem:
        this.onCopyCategoryToSystem.emit(this.defaultCategory);
        break;
    }
  }

  public storageEventListener(event: StorageEvent): void {
    if (!event) return;

    switch (event.key) {
      case GlobalConstants.MANAGE_MODE_SOURCE_SYSTEM_STATE:
        const data = JSON.parse(event.newValue);
        this.sysqueStateService.setManageSrcState(data);
        this.fittingList$ = of(this.state.fittingList);
        this.cd.detectChanges();
        break;
      case GlobalConstants.SYSTEM_CREATION_SUCCESS:
        if (
          event.newValue === GlobalConstants.TRUE.toLocaleLowerCase() &&
          localStorage.getItem(GlobalConstants.MANAGE_MODE_WINDOW)
        ) {
          this.storageEventCreateSystem();
        }
        break;
      case GlobalConstants.SYSTEM_DELETED_SUCCESS:
        if (
          event.newValue === GlobalConstants.TRUE.toLocaleLowerCase() &&
          localStorage.getItem(GlobalConstants.MANAGE_MODE_WINDOW)
        ) {
          this.storageEventDeleteSystem();
        }
        break;
      case GlobalConstants.SYSTEM_RENAMED_SUCCESS:
        if (
          event.newValue === GlobalConstants.TRUE.toLocaleLowerCase() &&
          localStorage.getItem(GlobalConstants.MANAGE_MODE_WINDOW)
        ) {
          this.storageEventRenameSystem();
        }
        break;
      case GlobalConstants.CATEGORY_CREATION_SUCCESS:
      case GlobalConstants.CATEGORY_RENAMED_SUCCESS:
      case GlobalConstants.CATEGORY_DELETED_SUCCESS:
        if (
          event.newValue === GlobalConstants.TRUE.toLocaleLowerCase() &&
          localStorage.getItem(GlobalConstants.MANAGE_MODE_WINDOW)
        ) {
          this.storageEventListenerForCategories(event.key);
        }
        break;
      case GlobalConstants.CanExecutePasteLogic:
        if (
          event.newValue === GlobalConstants.TRUE.toLocaleLowerCase() &&
          localStorage.getItem('ManageModeOperations')
        ) {
          this.onCategoryChanged({ value: this.state.selectedCategory, event: 'event from storage event listener' });
        }
        break;
      case GlobalConstants.LINE_GRAPHICS_SUCCESS:
        this.onCategoryChanged({ value: this.state.selectedCategory });
        break;
    }
  }

  //#region Category options

  /**
   * Handles storage events for category options
   * @param key - key to identify the storage event
   */
  private storageEventListenerForCategories(key: string): void {
    this.sysqueStateService.setManageSrcState(this.localStorageService.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY));
    this.state = this.localStorageService.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    // Handle category changes
    if (key === GlobalConstants.CATEGORY_RENAMED_SUCCESS) {
      this.state.selectedCategory = this.state.categoryList[this.selectedCategoryIndex].tabName;
      this.sysqueStateService.updateSrcProperty('selectedCategory', this.state.selectedCategory);
      if (this.isSameSystemSelected()) {
        this.sysqueStateService.updateDestProperty('selectedCategory', this.state.selectedCategory);
      }
    } else if (key === GlobalConstants.CATEGORY_DELETED_SUCCESS) {
      this.storageEventDeleteCategory();
    } else if (key === GlobalConstants.CATEGORY_CREATION_SUCCESS) {
      this.state.selectedCategory = this.state.categoryList[this.state.categoryList.length - 1].tabName;
      this.sysqueStateService.updateSrcProperty('selectedCategory', this.state.selectedCategory);
      if (this.isSameSystemSelected()) {
        this.sysqueStateService.updateDestProperty('selectedCategory', this.state.selectedCategory);
      }
    }

    this.defaultCategory = this.state.selectedCategory;

    // Update state
    this.onCategoryChanged({
      value: this.state.selectedCategory,
      event: 'event from storage event listener',
    });
    this.cd.markForCheck();
  }

  /**
   * Handles storage event for category deletion
   */
  private storageEventDeleteCategory(): void {
    // When a category is deleted, the selected category should be the first one
    this.state.selectedCategory = this.state.categoryList[0].tabName;
  }

  //#endregion

  //#region System operations

  /**
   * Handle the events for Create System
   */
  private storageEventCreateSystem(): void {
    // Select the newly created system
    this.state = this.localStorageService.getStorage(GlobalConstants.MANAGE_MODE_SOURCE_SYSTEM_STATE);
    const newSystem = this.state.systemList[this.state.systemList.length - 1].name;
    this.state.selectedSystem = newSystem;

    // Update the state
    this.sysqueStateService.updateSrcProperty('selectedSystem', this.state.selectedSystem);
    this.sysqueStateService.updateDestProperty('selectedSystem', this.state.selectedSystem);
    this.onValueChanged({
      value: this.state.selectedSystem,
      event: 'create',
    });
  }

  /**
   * Handle the events for Rename System
   */
  private storageEventRenameSystem(): void {
    const isSameSystemSelected = this.isSameSystemSelected();
    this.state = this.localStorageService.getStorage(GlobalConstants.MANAGE_MODE_SOURCE_SYSTEM_STATE);
    // Ensure the index is valid and points to the renamed system
    if (this.selectedSystemIndex >= 0 && this.selectedSystemIndex < this.state.systemList.length) {
      // Select the renamed system by using the updated name at this.selectedSystemIndex
      this.state.selectedSystem = this.state.systemList[this.selectedSystemIndex].name;
    }
  
    // Update the state and notify listeners
    this.sysqueStateService.updateSrcProperty('selectedSystem', this.state.selectedSystem);
    if (isSameSystemSelected) {
      this.sysqueStateService.updateDestProperty('selectedSystem', this.state.selectedSystem);
    }
    this.onValueChanged({
      value: this.state.selectedSystem,
      event: 'rename',
    });
    this.cd.detectChanges();
  }  

  /**
   * Handle the events for Delete System
   */
  private storageEventDeleteSystem(): void {
    const isSameSystemSelected = this.isSameSystemSelected();
    const currentSystemList = this.state.systemList;

    // If no systems remain after deletion
    if (currentSystemList.length === 0) {
        this.state.selectedSystem = null;
    } else if (this.selectedSystemIndex > 0) {
        // If the deleted system wasn't the first one, select the previous system
        this.state.selectedSystem = currentSystemList[this.selectedSystemIndex - 1].name;
    } else {
        // If the deleted system was the first one, select the new first system
        this.state.selectedSystem = currentSystemList[0].name;
    }
    this.sysqueStateService.updateSrcProperty('selectedSystem', this.state.selectedSystem);
    if (isSameSystemSelected) {
      this.sysqueStateService.updateDestProperty('selectedSystem', this.state.selectedSystem);
    }
    // Update the state
    this.onValueChanged({
        value: this.state.selectedSystem,
        event: 'delete',
    });
  }

  private isSameSystemSelected(): boolean {
    const destSelectedSystem = this.sysqueStateService.getManageDestState().selectedSystem;
    return this.state.selectedSystem === destSelectedSystem;
  }

  //#endregion

  public updateState(): void {
    this.sysqueStateService.setManageSrcState(this.state);
    this.sysqueStateService.setElectricalState(this.state);
    this.facade.updateLocalStorage(GlobalConstants.MANAGE_MODE_SOURCE_SYSTEM_STATE, this.state);
    this.cd.detectChanges();
  }
}
